import { Icon as SharedIcon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import {
  DEFAULT_EHR_TRIAL_DASHBOARD,
  EhrExploreStepId,
  EhrTrialDashboardContent,
  resolveEhrStepAction,
} from '@chiroup/core/types/EhrTrialDashboard.type';
import { TrialHomeContent } from '@chiroup/core/types/TrialHome.type';
import { ChevronDownIcon } from 'lucide-react';
import React from 'react';

/**
 * Read-only preview of the EHR trial dashboard — the home a trial user sees
 * when their intent is `ehr`. Shown instead of the card-home preview while an
 * EHR dashboard section is selected in the editor.
 *
 * Renders the state a prospect meets on their first visit: nothing explored,
 * so step 1 carries the recommended treatment. Progress is per user and lives
 * in the event log, so there is nothing to preview there.
 *
 * Styling tracks the live EhrTrialHome.tsx so "what marketing sees here"
 * matches "what users see live".
 */

type Props = {
  data: TrialHomeContent;
};

type Accent = 'primary' | 'orange' | 'amber' | 'violet' | 'blue';

const BADGE: Record<Accent, string> = {
  primary: 'bg-primary-600',
  orange: 'bg-orange-600',
  amber: 'bg-orange-500',
  violet: 'bg-violet-600',
  blue: 'bg-blue-600',
};

const TILE: Record<Accent, string> = {
  primary: 'bg-primary-50 dark:bg-primary-900/30',
  orange: 'bg-orange-50 dark:bg-orange-900/30',
  amber: 'bg-orange-50 dark:bg-orange-900/30',
  violet: 'bg-violet-50 dark:bg-violet-900/30',
  blue: 'bg-blue-50 dark:bg-blue-900/30',
};

const TITLE: Record<Accent, string> = {
  primary: 'text-primary-700 dark:text-primary-300',
  orange: 'text-orange-600 dark:text-orange-400',
  amber: 'text-orange-500 dark:text-orange-300',
  violet: 'text-violet-600 dark:text-violet-300',
  blue: 'text-blue-600 dark:text-blue-300',
};

const OUTLINE: Record<Accent, string> = {
  primary: 'border-primary-300 text-primary-700 dark:border-primary-700 dark:text-primary-300',
  orange: 'border-orange-300 text-orange-700 dark:border-orange-700 dark:text-orange-400',
  amber: 'border-orange-300 text-orange-600 dark:border-orange-700 dark:text-orange-300',
  violet: 'border-violet-300 text-violet-700 dark:border-violet-700 dark:text-violet-300',
  blue: 'border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-300',
};

const EhrTrialHomePreview: React.FC<Props> = ({ data }) => {
  const content: EhrTrialDashboardContent = data.ehrDashboard ?? DEFAULT_EHR_TRIAL_DASHBOARD;
  const essentialsCards = data.cards.filter(c => c.enabled && c.section === 'essentials');

  // Mirrors the live blocked-step rule through the SAME resolver the pages
  // use (resolveEhrStepAction): a step with no video and no URL configured
  // shows as Coming soon. Accents and the recommended rule mirror
  // EhrRoadmap.tsx so the preview can't drift from the page it stands in for.
  const comingSoon = (id: EhrExploreStepId) =>
    resolveEhrStepAction(content, id).kind === 'coming-soon';
  const steps = [
    {
      ordinal: '1',
      title: content.scheduleTitle,
      cta: content.scheduleCta,
      blocked: comingSoon('schedule'),
      accent: 'primary',
      recommended: true,
    },
    {
      ordinal: '2',
      title: content.saopInitialTitle,
      cta: content.saopInitialCta,
      blocked: comingSoon('saop-initial'),
      accent: 'orange',
      recommended: false,
    },
    {
      ordinal: '2b',
      title: content.followUpTitle,
      subtitle: content.followUpSupportingCopy,
      cta: content.followUpCta,
      blocked: comingSoon('follow-up'),
      accent: 'amber',
      recommended: false,
    },
    {
      ordinal: '3',
      title: content.billingTitle,
      cta: content.billingCta,
      blocked: comingSoon('billing'),
      accent: 'violet',
      recommended: false,
    },
    {
      ordinal: '4',
      title: content.reportingTitle,
      cta: content.reportingCta,
      blocked: comingSoon('reporting'),
      accent: 'blue',
      recommended: false,
    },
  ] as const;
  const availableCount = steps.filter(s => s.ordinal !== '2b').length;

  return (
    <div className="min-h-full bg-gray-50 pb-8 text-left dark:bg-dark-gray-900">
      <div className="border-b border-gray-200 bg-white px-5 pb-5 pt-5 dark:border-dark-gray-700 dark:bg-dark-gray-800 sm:px-6">
        <h1 className="text-xl font-bold text-gray-900 dark:text-dark-gray-100">Hello, Doctor</h1>
        <div className="mt-1 flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
            <SharedIcon icon={Icons.sparkles} className="h-3 w-3" />
            Trial · 14 days left
          </span>
          <span className="inline-flex items-center rounded bg-orange-600 px-2 py-0.5 text-[10px] font-semibold text-white">
            Select my plan
          </span>
        </div>
      </div>

      <div className="mt-6 px-4 sm:px-5">
        {/* Section 1 */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-dark-gray-700 dark:bg-dark-gray-800">
          <h2 className="text-base font-bold text-gray-900 dark:text-dark-gray-100">
            {content.saopHeading}
          </h2>
          <p className="mt-0.5 text-xs font-medium text-primary-600 dark:text-primary-300">
            {content.saopSubhead}
          </p>
          <p className="mt-2 text-xs text-gray-600 dark:text-dark-gray-300">
            {content.saopDescription}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-orange-600 px-3 py-1 text-[11px] font-semibold text-white">
              {content.saopPrimaryCta}
            </span>
            {content.saopVideoUrl ? (
              <span className="rounded-full border border-gray-200 px-3 py-1 text-[11px] font-semibold text-gray-700 dark:border-dark-gray-600 dark:text-dark-gray-200">
                {content.saopSecondaryCta}
              </span>
            ) : (
              <MissingNote text="No Why SAOP video set, so the secondary CTA is hidden" />
            )}
          </div>
        </div>

        {/* Section 2 */}
        <div className="mt-5 rounded-xl border border-gray-200 bg-white p-5 dark:border-dark-gray-700 dark:bg-dark-gray-800">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-dark-gray-100">
                {content.learnHeading}
              </h2>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-dark-gray-400">
                {content.learnSupportingCopy}
              </p>
            </div>
            <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-dark-gray-700 dark:text-dark-gray-300">
              0 of {availableCount} explored
            </span>
          </div>

          <ol className="mt-6 grid grid-cols-5 gap-2">
            {steps.map(step => (
              <li key={step.ordinal} className="flex flex-col items-center text-center">
                {/* Badge overlaps the circle, matching EhrRoadmap.tsx. */}
                <div className="relative">
                  <span
                    className={[
                      'absolute -left-1 -top-1 z-10 inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold text-white ring-2 ring-white dark:ring-dark-gray-800',
                      BADGE[step.accent],
                    ].join(' ')}
                  >
                    {step.ordinal}
                  </span>
                  <span
                    className={[
                      'inline-flex h-12 w-12 items-center justify-center rounded-full',
                      TILE[step.accent],
                    ].join(' ')}
                  />
                </div>
                <p
                  className={[
                    'mt-2 text-[10px] font-semibold leading-tight',
                    TITLE[step.accent],
                  ].join(' ')}
                >
                  {step.title}
                </p>
                {'subtitle' in step && step.subtitle && (
                  <p className="mt-0.5 text-[9px] leading-tight text-gray-500 dark:text-dark-gray-400">
                    {step.subtitle}
                  </p>
                )}
                <p
                  className={[
                    'mt-1.5 rounded border px-1.5 py-0.5 text-[9px] font-semibold',
                    step.blocked
                      ? `opacity-60 ${OUTLINE[step.accent]}`
                      : step.recommended
                        ? 'border-transparent bg-orange-600 text-white'
                        : `${OUTLINE[step.accent]}`,
                  ].join(' ')}
                >
                  {step.blocked ? 'Coming soon' : step.cta}
                </p>
              </li>
            ))}
          </ol>

          <div className="mt-4 rounded-lg border border-primary-200 bg-primary-100/50 p-3 dark:border-primary-900/40 dark:bg-primary-900/10">
            <p className="text-xs font-semibold text-gray-900 dark:text-dark-gray-100">
              {content.speedCalloutHeading}
            </p>
            <p className="mt-0.5 text-[11px] text-gray-600 dark:text-dark-gray-300">
              {content.speedCalloutCopy}
            </p>
          </div>
        </div>

        {/* Section 3 */}
        <div className="mt-5 rounded-xl border border-orange-200 bg-orange-50/60 p-5 dark:border-orange-900/40 dark:bg-orange-900/10">
          <h2 className="text-base font-bold text-gray-900 dark:text-dark-gray-100">
            {content.walkthroughHeading}
          </h2>
          <p className="mt-0.5 text-xs text-gray-600 dark:text-dark-gray-300">
            {content.walkthroughSupportingCopy}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="rounded-lg bg-orange-600 px-3 py-1.5 text-[11px] font-semibold text-white">
              {content.walkthroughCta} &rarr;
            </span>
            <div className="flex items-center gap-2">
              {content.walkthroughAvatars.length > 0 ? (
                <div className="flex -space-x-2">
                  {content.walkthroughAvatars.slice(0, 3).map(src => (
                    <img
                      key={src}
                      src={src}
                      alt=""
                      className="h-7 w-7 rounded-full border-2 border-white object-cover dark:border-dark-gray-800"
                    />
                  ))}
                </div>
              ) : (
                <MissingNote text="No faces uploaded" />
              )}
              <span className="max-w-[12rem] text-[11px] leading-snug text-gray-600 dark:text-dark-gray-300">
                {content.walkthroughSocialProof}
              </span>
            </div>
          </div>
        </div>

        {/* Section 4 */}
        <div className="mt-5 rounded-xl border border-gray-200 bg-white p-5 dark:border-dark-gray-700 dark:bg-dark-gray-800">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-dark-gray-100">
                {content.switchingHeading}
              </h2>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-dark-gray-400">
                {content.switchingSupportingCopy}
              </p>
            </div>
            <span className="shrink-0 text-[11px] font-semibold text-gray-400">Hide</span>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="flex items-start gap-3">
              <div className="relative h-[70px] w-[100px] shrink-0 overflow-hidden rounded-lg border border-gray-200 dark:border-dark-gray-700">
                {content.testimonialThumbnail ? (
                  <img
                    src={content.testimonialThumbnail}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center bg-gray-100 text-[9px] text-gray-400 dark:bg-dark-gray-700">
                    No poster
                  </span>
                )}
                {content.testimonialDuration && (
                  <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1 text-[9px] text-white">
                    {content.testimonialDuration}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold leading-snug text-gray-900 dark:text-dark-gray-100">
                  &ldquo;{content.testimonialHeadline}&rdquo;
                </p>
                {content.testimonialAuthor && (
                  <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-gray-400">
                    {content.testimonialAuthor}
                  </p>
                )}
                {content.testimonialSupportingCopy && (
                  <p className="mt-1 text-[11px] leading-snug text-gray-500 dark:text-dark-gray-400">
                    {content.testimonialSupportingCopy}
                  </p>
                )}
                {content.testimonialVideoUrl ? (
                  <p className="mt-1.5 text-[11px] font-semibold text-primary-700 dark:text-primary-300">
                    {content.testimonialCta} &rarr;
                  </p>
                ) : (
                  <div className="mt-1.5">
                    <MissingNote text="No video set, so the play control is hidden" />
                  </div>
                )}
              </div>
            </div>

            <div className="divide-y divide-gray-100 dark:divide-dark-gray-700">
              {content.faqs.map((faq, idx) => (
                <div
                  key={`${faq.question}-${idx}`}
                  className="flex items-center justify-between gap-2 py-2"
                >
                  <p className="text-xs font-medium text-gray-900 dark:text-dark-gray-100">
                    {faq.question || 'Untitled question'}
                  </p>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {!faq.answer && <MissingNote text="No answer" />}
                    <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-primary-100 bg-primary-50/50 p-4 dark:border-primary-900/40 dark:bg-primary-900/10">
            <p className="text-xs font-semibold text-gray-900 dark:text-dark-gray-100">
              {content.reassuranceHeading}
            </p>
            <p className="mt-0.5 text-[11px] text-gray-600 dark:text-dark-gray-300">
              {content.reassuranceCopy}
            </p>
            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              {content.reassuranceProofPoints.map((point, idx) => (
                <li
                  key={`${point}-${idx}`}
                  className="text-[11px] text-gray-700 dark:text-dark-gray-200"
                >
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Section 5 */}
        <div className="mt-5 rounded-xl border border-gray-200 bg-white p-5 dark:border-dark-gray-700 dark:bg-dark-gray-800">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-primary-600 dark:text-primary-300">
                {content.essentialsEyebrow}
              </p>
              <h2 className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-dark-gray-100">
                {content.essentialsHeading}
              </h2>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-dark-gray-400">
                {content.essentialsSupportingCopy}
              </p>
            </div>
            <span className="shrink-0 rounded-full border border-gray-200 px-3 py-1 text-[11px] font-semibold text-gray-700 dark:border-dark-gray-600 dark:text-dark-gray-200">
              {content.essentialsCta}
            </span>
          </div>
          {/* Collapsed on the live page; shown here so the categories and the
              cards they sit above are both reviewable. */}
          <div className="mt-3 border-t border-gray-100 pt-3 dark:border-dark-gray-700">
            <div className="flex flex-wrap gap-1.5">
              {content.essentialsCategories.map((category, idx) => (
                <span
                  key={`${category}-${idx}`}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-[10px] font-medium text-gray-700 dark:border-dark-gray-700 dark:bg-dark-gray-800 dark:text-dark-gray-200"
                >
                  {category}
                </span>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-gray-400 dark:text-dark-gray-500">
              Reveals the {essentialsCards.length} enabled Essentials card
              {essentialsCards.length === 1 ? '' : 's'} configured above.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Flags content the live page will hide or disable until it is filled in. */
const MissingNote: React.FC<{ text: string }> = ({ text }) => (
  <span className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
    {text}
  </span>
);

export default EhrTrialHomePreview;
